import Constants from 'Constants'
import { EventBus } from 'EventBus'
import Common from '@/assets/js/common.js'
import Queue from '@/assets/js/utils/queue'

import ConfigMixin from "@/mixins/Config.js"
import Config from '@/mixins/Config.js'

import SyncWorker from 'comlink-loader!@/assets/js/sync/DataProcessor.js'
import { SYNC_STATES, PROGRESS_STATES, RECORD_STATES, FILE_TYPES, MEDIA_STATES } from '@/assets/js/sync/states'
import * as Comlink from 'comlink'
import GroomyDB from '@/assets/js/dexie/GroomyDB.js'
import { resetCachedCleaners } from '@/assets/js/cache'
import CacheManager from '@/assets/js/cache/CacheManager'

import _cloneDeep from 'lodash/cloneDeep'
import _debounce from 'lodash/debounce'
import _defaults from 'lodash/defaults'
import _merge from 'lodash/merge'

export default {
    install (Vue, { store }) {
        const SyncPlugin = {
            /*************
             * VARIABLES *
             *************/
            /**
             * Timestamp de dernière synchro des Required et Optionnal
             */
			_lastSync: {},
			/**
             * Params de pagination de la dernière synchro des Required
             */
            _lastRequiredState: null,
			/**
             * Params de pagination de la dernière synchro des données Optional
             */
            _lastOptionnalState: null,
            /**
             * Indique si un export de la BDD a été demandé par l'API
             */
            _needExport: false,
            /**
             * ID du navigateur qui sert de référence pour la synchro
             */
            _deviceId: null,
            _wsSessionId: null,
            /**
             * ID du timer window.setInterval
             */
            _timerId: null,
            /**
             * Fonctions d'annnulation des requetes axios
             */
            _cancelFns: {},
            /**
             * IDs replicated pour mettre à jour les clés
             * étrangères et primaires à la volée
             */
			_replicated: {},
			/**
             * Worker's class designed to handle
             * synchronized data
             */
			_processor: null,
			/**
			 * Instance du Web Worker Comlink (pour pouvoir le terminer proprement)
			 */
			_workerInstance: null,
			/**
			 * Elements en attente d'insertion dans _sync_temp
			 */
			_pendingRecords: [],

			_debouncedForce: () => {},

            _queue : new Queue(),

			_handleDataQueue : new Queue(),

			/**
			* websocket permetant de forcer rapidement la réplication de synchro d'un autre client de la même licence
			*/
			_ws: null,
			/**
			 * Flag pour autoriser/bloquer les reconnexions WebSocket
			 */
			_wsReconnectEnabled: true,
			/**
			 * Timer de reconnexion WebSocket (pour pouvoir l'annuler)
			 */
			_wsReconnectTimer: null,

			/***********************
			 * WS SYNC OPTIMIZATION *
			 ***********************/
			/**
			 * Flag indiquant qu'une synchro WS est en attente (coalescing)
			 * Toutes les demandes WS pendant une synchro sont fusionnées en une seule
			 */
			_wsSyncPending: false,
			/**
			 * Flag indiquant qu'une synchro WS est en cours de traitement
			 */
			_wsSyncProcessing: false,
			/**
			 * Fonction debouncée pour les messages WS (sera initialisée dans init())
			 */
			_debouncedWsSync: null,
			/**
			 * Délai de debounce pour les messages WS (en ms)
			 */
			WS_DEBOUNCE_DELAY: 500,

			PACKET_SIZE: 750,

			/***********************
			 * SYNC DEBUG LOGGING *
			 ***********************/
			_syncDebug: {
				enabled: process.env.VUE_APP_SYNC_DEBUG === 'true', // Contrôlé via .env
				callId: 0,
				wsMessages: [],
				forcesCalls: [],
				askCalls: [],
				coalescedCalls: 0, // Compteur d'appels fusionnés (appels évités)
				startTime: null
			},

			_logSync(type, message, data = {}) {
				if (!this._syncDebug.enabled) return
				
				const now = Date.now()
				const elapsed = this._syncDebug.startTime ? now - this._syncDebug.startTime : 0
				const callId = ++this._syncDebug.callId
				
				const logEntry = {
					callId,
					type,
					message,
					timestamp: now,
					elapsed,
					progressState: store.state.sync.progressState,
					...data
				}
				
				const colors = {
					'WS_RECV': 'background: #ff9800; color: white; padding: 2px 6px; border-radius: 3px;',
					'WS_ASK': 'background: #ff5722; color: white; padding: 2px 6px; border-radius: 3px;',
					'WS_DEBOUNCE': 'background: #795548; color: white; padding: 2px 6px; border-radius: 3px;',
					'WS_COALESCE': 'background: #607d8b; color: white; padding: 2px 6px; border-radius: 3px;',
					'WS_PROCESS': 'background: #009688; color: white; padding: 2px 6px; border-radius: 3px;',
					'WS_SKIP': 'background: #9e9e9e; color: white; padding: 2px 6px; border-radius: 3px;',
					'FORCE_START': 'background: #4caf50; color: white; padding: 2px 6px; border-radius: 3px;',
					'FORCE_WAIT': 'background: #f44336; color: white; padding: 2px 6px; border-radius: 3px;',
					'FORCE_END': 'background: #2196f3; color: white; padding: 2px 6px; border-radius: 3px;',
					'ASK_START': 'background: #9c27b0; color: white; padding: 2px 6px; border-radius: 3px;',
					'ASK_WAIT': 'background: #e91e63; color: white; padding: 2px 6px; border-radius: 3px;',
					'ASK_END': 'background: #00bcd4; color: white; padding: 2px 6px; border-radius: 3px;'
				}
				
				console.log(
					`%c[SYNC #${callId}] ${type}`,
					colors[type] || 'background: gray; color: white;',
					`+${elapsed}ms |`,
					message,
					data
				)
				
				return callId
			},

			/**
			 * Réinitialise les compteurs de debug de synchronisation.
			 * Remet à zéro les statistiques d'appels WS, force() et ask().
			 */
			_resetSyncDebug() {
				this._syncDebug.startTime = Date.now()
				this._syncDebug.callId = 0
				this._syncDebug.wsMessages = []
				this._syncDebug.forcesCalls = []
				this._syncDebug.askCalls = []
				this._syncDebug.coalescedCalls = 0
				console.log('%c[SYNC] === DEBUG SESSION RESET ===', 'background: black; color: lime; padding: 4px 8px;')
			},

			/**
			 * Affiche un rapport récapitulatif de la session de debug.
			 * Indique le nombre d'appels WS reçus, les appels fusionnés et le temps économisé.
			 */
			_printSyncReport() {
				const wsCount = this._syncDebug.wsMessages.length
				const askCount = this._syncDebug.askCalls.length
				const coalescedCount = this._syncDebug.coalescedCalls
				const savedCalls = wsCount - askCount
				
				console.group('%c[SYNC REPORT]', 'background: #333; color: white; padding: 4px 8px; font-size: 14px;')
				console.log('Total WS messages reçus:', wsCount)
				console.log('Total force() calls:', this._syncDebug.forcesCalls.length)
				console.log('Total ask() calls effectués:', askCount)
				console.log('Appels fusionnés (coalesced):', coalescedCount)
				
				if (savedCalls > 0) {
					console.log('%c\u2714 Appels évités grâce au debounce+coalescing: ' + savedCalls, 'color: #4caf50; font-weight: bold;')
					console.log('%c\u2714 Temps estimé économisé: ~' + (savedCalls * 400) + 'ms', 'color: #4caf50; font-weight: bold;')
				}
				
				console.log('Durée totale du test:', Date.now() - this._syncDebug.startTime, 'ms')
				console.groupEnd()
			},

            /********
             * MAIN *
             ********/
            /**
             * Initialise le plugin
             */
            async init() {
				this._lastSync.required = this.getLastSync('required')
				this._lastRequiredState = this.getLastRequiredState()
                this._lastSync.optionnal = this.getLastSync('optionnal')
				this._lastOptionnalState = this.getLastOptionnalState()
                this._needExport = this._getNeedExport()
				this._replicated = this._getReplicated()
				this._debouncedForce = _debounce(async () => {
					if (store.state.sync.progressState === PROGRESS_STATES.COMPLETED) {
						return this.force(true)
					}
				}, 500)

				// Si des lignes n'ont pas pu etre insérées dans _sync_temp à la dernière utilisation
				this._pendingRecords = this._getPendingRecords()
				await this.persistPendingRecords()
				this._setPendingRecords(this._pendingRecords)

				// Création du Web Worker et stockage de l'instance pour pouvoir le terminer proprement
				const inst = new SyncWorker()
				this._workerInstance = inst
				this._processor = await new inst.DataProcessor(
					Comlink.proxy(this._setSyncActualModelsState)
				)
				await this._processor.init()

				// TODO: A supprimer en 09/2021 lorsqu'on considèrera toutes les lignes renvoyées
				// Décaler l'envoi des lignes non synchronisées pour ne pas ralentir le chargement
				setTimeout(() => {
					this._processor.syncMissingRows()
				}, 10000)

				this.runFromScratchIfNeeded()

				window.addEventListener('focus', () => {
 					//this.onFocus()
				})
				window.addEventListener('beforeunload', () => {
 					this.onBeforeUnload()
				})

				this.websocketInit()

				// Initialiser la fonction debouncée pour les messages WS
				this._debouncedWsSync = _debounce(() => {
					this._processWsSyncRequest()
				}, this.WS_DEBOUNCE_DELAY)

				// Exposer les fonctions de debug sur window uniquement si activé via .env
				if (this._syncDebug.enabled) {
					this._resetSyncDebug()
					window.$syncDebug = {
						reset: () => this._resetSyncDebug(),
						report: () => this._printSyncReport(),
						enable: () => { this._syncDebug.enabled = true; console.log('[SYNC] Debug activé') },
						disable: () => { this._syncDebug.enabled = false; console.log('[SYNC] Debug désactivé') },
						status: () => console.log('[SYNC] Debug:', this._syncDebug),
						
						// Fonctions de simulation WebSocket
						simulateWs: () => {
							console.log('%c[SYNC TEST] Simulation message WebSocket "sync"', 'background: #ff6600; color: white; padding: 2px 6px;')
							this._onWebSocketSyncMessage()
						},
						simulateWsBurst: (count = 5, intervalMs = 50) => {
							console.log(`%c[SYNC TEST] Simulation rafale de ${count} messages WS (intervalle: ${intervalMs}ms)`, 'background: #ff6600; color: white; padding: 2px 6px;')
							for (let i = 0; i < count; i++) {
								setTimeout(() => {
									console.log(`%c[SYNC TEST] Message WS #${i + 1}/${count}`, 'background: #ff6600; color: white; padding: 2px 6px;')
									this._onWebSocketSyncMessage()
								}, i * intervalMs)
							}
						},
						testForceWithWsBurst: async (wsCount = 5, wsIntervalMs = 50, wsDelayMs = 100) => {
							console.log('%c[SYNC TEST] ═══════════════════════════════════════════════════', 'background: #9900ff; color: white; padding: 2px 6px;')
							console.log(`%c[SYNC TEST] Test: force() + rafale de ${wsCount} WS après ${wsDelayMs}ms`, 'background: #9900ff; color: white; padding: 2px 6px;')
							console.log('%c[SYNC TEST] ═══════════════════════════════════════════════════', 'background: #9900ff; color: white; padding: 2px 6px;')
							
							// Reset les compteurs
							this._resetSyncDebug()
							
							// Lancer force() et la rafale WS en parallèle
							const forcePromise = this.force()
							
							// Après un délai, simuler une rafale de WS
							setTimeout(() => {
								window.$syncDebug.simulateWsBurst(wsCount, wsIntervalMs)
							}, wsDelayMs)
							
							// Attendre que force() termine
							await forcePromise
							
							console.log('%c[SYNC TEST] force() terminé, attendons 2s pour voir les ask() empilés...', 'background: #9900ff; color: white; padding: 2px 6px;')
							
							// Attendre un peu pour voir les ask() s'empiler
							await new Promise(resolve => setTimeout(resolve, 10000))
							
							console.log('%c[SYNC TEST] ═══════════════════════════════════════════════════', 'background: #9900ff; color: white; padding: 2px 6px;')
							this._printSyncReport()
						}
					}
					console.log('%c[SYNC DEBUG] Commandes disponibles:', 'background: #333; color: lime; padding: 4px 8px;')
					console.log('%c  $syncDebug.reset()              - Réinitialise les compteurs', 'color: lime;')
					console.log('%c  $syncDebug.report()             - Affiche le rapport', 'color: lime;')
					console.log('%c  $syncDebug.enable/disable()     - Active/désactive les logs', 'color: lime;')
					console.log('%c  $syncDebug.simulateWs()         - Simule 1 message WS', 'color: orange;')
					console.log('%c  $syncDebug.simulateWsBurst(n,ms)- Simule n messages WS', 'color: orange;')
					console.log('%c  $syncDebug.testForceWithWsBurst()- TEST COMPLET', 'color: #ff00ff; font-weight: bold;')
				}
            },
            /**
             * Rends la synchronisation clean, notamment au delog/switch de licence
             */
            async reset() {
				if(this._processor) {
					await this._processor.reset()
					this._processor = null
				}

				// Terminer proprement le Web Worker
				if(this._workerInstance) {
					try {
						if(typeof this._workerInstance.terminate === 'function') {
							this._workerInstance.terminate()
						}
					} catch(e) {
						// Ignorer silencieusement les erreurs de terminaison
					}
					this._workerInstance = null
				}

				// Fermeture propre du WebSocket
				this.websocketClose()

				resetCachedCleaners()

                await this.stop()
            },

            /**
             * Démarre la tâche de synchronisation
             */
            start() {
				// Nettoyage préventif si un intervalle existe déjà (évite les timers orphelins)
				if (this._timerId !== null) {
					window.clearInterval(this._timerId)
					this._timerId = null
				}

				store.commit('sync/setState', SYNC_STATES.STARTED)
				this._getSqliteDatabaseExport()

                this._timerId = setInterval(() => {
					if (store.state.sync.progressState === PROGRESS_STATES.COMPLETED
						&& window.navigator.onLine
						&& document.hasFocus()
					) {
                        this.force(true)
                    }
                }, process.env.VUE_APP_SYNC_INTERVAL || 30000)

                return this.force(true)
            },
            /**
             * Arrête la tâche de synchronisation
             */
            async stop() {
				window.clearInterval(this._timerId)
				this._timerId = null
                this._cancelRequests()

				store.commit('sync/setState', SYNC_STATES.STOPPED)
				if (store.state.sync.progressState !== PROGRESS_STATES.COMPLETED) {
					store.commit('sync/setProgressState', PROGRESS_STATES.CANCELLED)
					await Common.waitUntil(() => (store.state.sync.progressState === PROGRESS_STATES.COMPLETED))
				}
            },

            /********
             * DATA *
             ********/

            /**
             * Fonction principale pour la gestion de la synchronisation
             */
            async force(withOptional=false, onlyOptional=false) {
				const forceStartTime = Date.now()
				const forceCallId = this._logSync('FORCE_START', 'force() appelé', { withOptional, onlyOptional })
				this._syncDebug.forcesCalls.push({ callId: forceCallId, startTime: forceStartTime })

				// On vérifie si il est en ligne ou si l'onglet est actif
                if (!window.navigator.onLine) {
                    store.commit('sync/setState', SYNC_STATES.OFFLINE)
					this._logSync('FORCE_END', 'force() abandonné - OFFLINE', { duration: Date.now() - forceStartTime })
                    return
				}

                if (store.state.sync.progressState !== PROGRESS_STATES.COMPLETED) {
					const waitStartTime = Date.now()
					this._logSync('FORCE_WAIT', 'force() BLOQUÉ - attend fin synchro en cours', { currentState: store.state.sync.progressState })
                    this._cancelRequests()
                    await Common.waitUntil(() => (store.state.sync.progressState === PROGRESS_STATES.COMPLETED))
					this._logSync('FORCE_WAIT', `force() DÉBLOQUÉ après ${Date.now() - waitStartTime}ms d'attente`, { waitDuration: Date.now() - waitStartTime })
                }

                try {
                    store.commit('sync/setProgressState', PROGRESS_STATES.SENDING)

                    if(!onlyOptional) {
						this._lastRequiredState = this.getLastRequiredState()
						await this._run('required', this._lastRequiredState)
					}

                    if (withOptional) {
						this._lastOptionnalState = this.getLastOptionnalState()
						await this._run('optionnal', this._lastOptionnalState)
					}

                    this._onSyncSuccess()
                } catch(e) {
					if (e.message !== 'Synchronisation cancelled') {
						// this._onSyncError(e)
						console.error(e);
					}
                } finally {
					this._logSync('FORCE_END', `force() terminé en ${Date.now() - forceStartTime}ms`, { totalDuration: Date.now() - forceStartTime })
                    this._onSyncEnd()
                }
			},
            /**
             * Demande une synchronisation mais sans la forcer
             */
            async ask() {
				const askStartTime = Date.now()
				const askCallId = this._logSync('ASK_START', 'ask() appelé (depuis WS)', {})
				this._syncDebug.askCalls.push({ callId: askCallId, startTime: askStartTime })

				// On vérifie si il est en ligne ou si l'onglet est actif
                if (!window.navigator.onLine) {
                    store.commit('sync/setState', SYNC_STATES.OFFLINE)
					this._logSync('ASK_END', 'ask() abandonné - OFFLINE', { duration: Date.now() - askStartTime })
                    return
				}

                if (store.state.sync.progressState !== PROGRESS_STATES.COMPLETED) {
					const waitStartTime = Date.now()
					this._logSync('ASK_WAIT', 'ask() BLOQUÉ - attend fin synchro en cours', { currentState: store.state.sync.progressState })
                    await Common.waitUntil(() => (store.state.sync.progressState === PROGRESS_STATES.COMPLETED))
					this._logSync('ASK_WAIT', `ask() DÉBLOQUÉ après ${Date.now() - waitStartTime}ms d'attente`, { waitDuration: Date.now() - waitStartTime })
                }

                try {
                    store.commit('sync/setProgressState', PROGRESS_STATES.SENDING)

					this._lastRequiredState = this.getLastRequiredState()
					await this._run('required', this._lastRequiredState)

					this._lastOptionnalState = this.getLastOptionnalState()
					await this._run('optionnal', this._lastOptionnalState)

                    this._onSyncSuccess()
                } catch(e) {
					if (e.message !== 'Synchronisation cancelled') {
						// this._onSyncError(e)
						console.error(e);
					}
                } finally {
					this._logSync('ASK_END', `ask() terminé en ${Date.now() - askStartTime}ms`, { totalDuration: Date.now() - askStartTime })
                    this._onSyncEnd()
                }
			},
			/**
			 * Synchronisation montante des médias
			 */
			async _runMedias() {
				const medias = await this._retrieveMedias()
				const table = await Vue.prototype.$storage.db.t('_files')

				await Common.asyncForEach(
					medias,
					async (media) => {
						try {
							await table.update(media.filename, {
								state: MEDIA_STATES.UPLOADING
							})

							const url = Constants.SYNC_FILE_API
							const formData = new FormData()
							let mediaContent = media.content

							if(!(mediaContent instanceof Blob)) {
								const temp = new Uint8Array(mediaContent)
								mediaContent = new Blob([temp], { type: media.filetype })
							}

							formData.append('media', mediaContent)
							formData.append('media_filename', media.filename)

							await this._sendRequest(
								url,
								formData,
								true,
								{ 'Content-Type': 'multipart/form-data' }
							)

							await table.update(media.filename, {
								state: MEDIA_STATES.DOWNLOADED,
								last_used: new Date()
							})
						}
						catch (err) {
							await table.update(media.filename, {
								state: MEDIA_STATES.NEED_UPLOAD
							})
							throw err
						}
					}
				)
			},
			async runUp(type='required', wishlist=null, opts={}, since=undefined) {

				await this._sendExportIfNeeded()

				// Il faut envoyer les médias avant les données pour éviter des documents pas trouvés
				await this._runMedias()

				// Récupérer les X premiers records de _sync_temp
				const syncTempRows = await this._retrieveUpdatedRecords()
				const needNextPacket = syncTempRows.length >= this.PACKET_SIZE

				// Transformation pour correspondre au format de l'API
				const records = []
				const syncTempIds = []

				syncTempRows.forEach((row) => {
					records.push({
						model: row.table_name,
						id: row.row_id,
						operation: row.operation,
						data: row.columns
					})

					syncTempIds.push(row.id)
				})


				// Pour ne pas récupérer la synchro descendante
				if(needNextPacket) {
					records.push({
						only_models: []
					})
				}
				else if (wishlist) {
					records.push({
						only_models: wishlist
					})
				}

				const response = await this._getData(type, {
					...records,
					...opts
				}, since)

				const data = response.data.retour
				data.replicated = data.replicated || []

				// si la synchro me retourne un token alors je le set dans la config et le header des prochaines requêtes
				if(data.token) {
					await ConfigMixin.methods.setConfig("token", data.token)
					Vue.axios.defaults.headers['Authorization'] = 'Bearer ' + data.token
				  	Vue.axios.defaults.headers.common['Authorization'] = 'Bearer ' + data.token
				}

				// Appliquer les replicated
				if(data.replicated.length > 0) {
					// On modifie les lignes dans l'IndexedDB pour virer les lignes négatives
					await this._processor.applyReplicated(data.replicated)

					// On va mettre à jour en RAM les replicated
					this._updateReplicated(data.replicated)
				}

				await this._deleteUpdatedRecords(syncTempIds)

				//si l'on a envoyé des données on lance un ping ws
				if(records.length > 0){
					this.websocketPush()
				}

				if(needNextPacket) {
					return this.runUp(type, wishlist, opts, since)
				}
				else {
					return response.data.retour
				}
			},
			/**
			 * Exécute la synchro montante et récupère les éventuelles données du type spécifié
			 * @param {String} type required ou optionnal en fonction du type de synchro voulu
			 * @param {Object} pagination Optionel - Indique l'avancement de la synchronisation
			 * @param {Array} wishlist Optionel - Synchroniser seulement une partie des tables
			 */
            async _run(type, pagination=undefined, wishlist=undefined, since=undefined, id="default") {
				const result = await this.runUp(type, wishlist, {
					pagination: pagination || undefined
				}, since)

				await this._handleData(result, type, id)

				// Quand tous les packets sont reçus, on a plus de pagination
				if(result.pagination == null) {
					// Comme on a récupéré seulement une partie des données, on doit pas se souvenir de la timestamp
					if (!wishlist) {
						store.commit('sync/setProgressState', PROGRESS_STATES.SENDING)
						this.setLastSync(type, result.timestamp)
					}
				}
				// Si on a une pagination, on relance avec les params de pagination
				else if(store.state.sync.progressState !== PROGRESS_STATES.CANCELLED) {
					store.commit('sync/setProgressState', PROGRESS_STATES.SENDING)
					return this._run(type, result.pagination, wishlist, since, id)
				}
				else {
					this._onSyncEnd()
				}
			},
			/**
			 * Exécute la synchronisation montante et récupère seulement les tables nécessaires
			 * @param {Array} whishlist
			 */
			async runRequiredWhishlist(whishlist, since=undefined, id) {
				if(!id) {
					id = Common.getRandomString(5)
				}
				return this._run('required', undefined, whishlist, since, id)
			},
			/**
			 * Écrit en base locale des lignes déjà connues du serveur, sans passer par une synchronisation.
			 *
			 * DataProcessor marque la transaction comme venant de la synchro, ce qui exclut ces lignes de
			 * la synchro montante, et met à jour les index de cache comme le ferait une synchro descendante.
			 * L'écriture passe par la même file que _handleData pour ne pas croiser une synchro en cours.
			 *
			 * @param {String} tableName Table visée
			 * @param {Array} records Lignes telles que l'API les renvoie
			 */
			async storeRecords(tableName, records) {
				if(records.length === 0) {
					return
				}

				return this._handleDataQueue.enqueue(() => this._processor.processData({
					inserted: { [tableName]: records },
					updated: {},
					deleted: {}
				}))
			},
			/**
			 * Exécute la synchronisation montante et récupère seulement les tables nécessaires pour les optional
			 * @param {Array} whishlist
			 */
			async runOptionalWhishlist(whishlist, since=undefined, id=undefined) {
				if(!id) {
					id = Common.getRandomString(5)
				}
				return this._run('optionnal', undefined, whishlist, since, id)
			},
			async runFromScratchIfNeeded() {
				const syncFromScratch = this.getSyncFromScratch()
				if(syncFromScratch.length > 0) {
					await this.runRequiredWhishlist(syncFromScratch, 1, 'from_scratch')
					await this.runOptionalWhishlist(syncFromScratch, 1, 'from_scratch')
					this.setSyncFromScratch([])
				}
			},
			/**
			 * Formatte l'URL de synchronisation des médias
			 * @param {String} filename
			 * @return {String} URL à requeter pour envoyer un média vers l'API
			 */
			_constructUploadMediaUrl(filename) {
				return Constants.SYNC_FILE_API
					+ `?licence_key=${Constants.USER_LICENCE_KEY}`
					+ `&device_id=${this.deviceId()}`
					+ `&media_filename=${filename}`
			},
			/**
			 * Récupère les médias en attente de synchronisation
			 * @returns {Array} Liste de fichiers de la table _files
			 */
			async _retrieveMedias() {
				return Vue.prototype
				.$storage
				.db
				.t('_files')
				.then(table => {
					return table.where({
						state: MEDIA_STATES.NEED_UPLOAD
					})
				})
				.then(col => {
					return col.toArray()
				})
			},
			async _sendExportIfNeeded() {
				if (this._needExport) {
					let tableItems = {}

					tableItems['_sync_temp'] = await Vue.prototype.$storage.db.t('_sync_temp')
					.then(table => {
						return table.toArray()
					})

					await Common.asyncForEach(
						Vue.prototype.$storage.db.tables,
						async (table) => {
							if (table.name.startsWith('_')) {
								return
							}

							const items = await table.where(':id').below(0).toArray()
							if(items.length) {
								tableItems[table.name] = items
							}
						}
					)

					await Vue.prototype.$sync.sendExport({
						licence_key: Constants.USER_LICENCE_KEY,
						deviceId: this.deviceId(),
						source: 'Sync::_retrieveUpdatedRecords',
						data: {
							tableItems,
						}
					})

					this._needExport = false
				}
			},
			/**
			 * Récupère les données en attente de synchronisation
			 * @returns {Array} Liste de lignes de la table _sync_temp
			 */
            async _retrieveUpdatedRecords() {
                // Récupération des modifications à envoyer
                return Vue.prototype
                .$storage
                .db
                .t('_sync_temp')
                .then(table => {
                    return table.limit(this.PACKET_SIZE).toArray()
				})
			},
			/**
			 * Lorsque la synchronisation a réussi, on supprime les données en attente d'envoie
			 */
            _deleteUpdatedRecords(syncTempIds) {
                return Vue.prototype
                .$storage
                .db
                .t('_sync_temp')
                .then(table => {
                    return table
                        .where('id')
						.anyOf(syncTempIds)
                        .delete()
                })
			},
			/**
			 * Traite les données reçues par l'API
			 * @param {Object} data Résultat de la requete de synchro de l'API
			 * @param {String} type Type de synchronisation - required ou optionnal
			 */
            async _handleData(data, type='required', id="default") {
                if (data.status !== 'ok') {
                    throw new Error(`Received a sync success with a <${data.status}> status`)
				}

				// On l'ajoute à une queue comme ça si un syncWishlist tourne en meme
				// temps les traitements vont se faire un par un
				await this._handleDataQueue.enqueue(async () => {

					_defaults(data, {
						type: type || 'required',
						pagination: {},
						inserted: {},
						updated:  {},
						deleted:  {},
						indexation: {}
					})

					// Processing inserted, updated & deleted
					await this._processor.processData(data)

					// Ne pas save la pagination si c'est la dernière page pour ne pas la renvoyer à la prochaine synchro
					if(data.pagination.model_page === 0) {
						data.pagination = null
					}

					// On se souvient de la pagination
					if(type == 'required') {
						this.setLastRequiredState(id, data.pagination)
					}
					else {
						this.setLastOptionnalState(id, data.pagination)
					}

					data.indexation.forEach(data => {
						CacheManager.inst().onObjectsMutation(data.table_name, [data.item_id], data.columns)
					})
				})
			},

			/**
			 * Permet de sauvegarder dans le Plugin et dans le localStorage les correspondances d'IDs
			 * @param {Array} replicatedRes Infos des données répliquées
			 */
            _updateReplicated(replicatedRes=[]) {
                if(replicatedRes.length === 0) {
                    return
				}

                replicatedRes.forEach(res => {
                    const primKeyName = Object.keys(res).find(key => (key !== 'old_id' && key.endsWith('_id')))
                    const pk = `${res.model}|${res.old_id}`
                    if (!this._replicated[pk]) {
                        this._replicated[pk] = {
                            value: res[primKeyName],
                            date: new Date()
                        }
                    }
                })
                this._setReplicated(this._replicated)
            },

            /**
             * Retourne le bon ID pour la ligne de la table correspondante
             * @param {String} tableName Nom de la table avec la clé
             * @param {Array | Number} val Valeur actuelle de la clé
             */
            replaceWithReplicated(tableName, ids) {
                const singleItem = !Array.isArray(ids)

                if (singleItem) {
                    ids = [ids]
                }

                ids = ids.map(val => {
					const id = parseInt(val)
                    if (!Number.isInteger(id)) {
                        return val
					}
					else if (id > 0) {
						return parseInt(id)
					}

                    const result =  this._replicated[`${tableName}|${id}`]
                    return result ? result.value : id
                })

                return singleItem ? ids[0] : ids
			},

			onFocus() {
				this._replicated = this._getReplicated()

				if (store.state.sync.progressState === PROGRESS_STATES.COMPLETED
					&& store.state.sync.state === SYNC_STATES.STARTED
				) {
					this.force(true)
				}
			},

			onBeforeUnload() {
				let oldPendingRecords = this._getPendingRecords()
				this._pendingRecords = oldPendingRecords.concat(this._pendingRecords)

				this._setPendingRecords(this._pendingRecords)
			},

			async addPendingRecords(records) {
				this._pendingRecords = this._pendingRecords.concat(records)

				await this.persistPendingRecords()

				this._debouncedForce(true)
			},

			async persistPendingRecords() {
                return this._queue.enqueue( async () => {
                    const records = this._pendingRecords.concat([])

                    if(records.length === 0) {
                        return
                    }

                    await Vue.prototype
                    .$storage
                    .db
                    .t('_sync_temp')
                    .then(table => {
                        return table.bulkAdd(records)
                    })

                    // Supprimer les X premiers éléments qu'on vient de save dans _sync_temp
                    this._pendingRecords.splice(0, records.length)
        		})
			},

            /**********
             * EVENTS *
             **********/
            _onSyncSuccess() {
				if (store.state.sync.state !== SYNC_STATES.STARTED) {
					store.commit('sync/setState', SYNC_STATES.STARTED)
				}

				EventBus.$emit('sync:progress_success')
            },
            _onSyncError(e) {
                store.commit('sync/setState', SYNC_STATES.ERROR)
                EventBus.$emit('sync:progress_error')
                console.error(e)
            },
            _onSyncEnd() {
                store.commit('sync/setProgressState', PROGRESS_STATES.COMPLETED)
				localStorage.removeItem("websocket:sync")
            },

            /************
             * REQUESTS *
             ************/
            _cancelRequests() {
                Object.keys(this._cancelFns).forEach(requestId => {
                    this._cancelFns[requestId]('Synchronisation cancelled')
                })
            },
            /**
             * Envoyer une requête annulable au serveur
             */
            _sendRequest(url, data={}, cancellable=true, opts={}) {
				const requestId = this.uuid()

				if (data instanceof FormData) {
					data.append('licence_key', Constants.USER_LICENCE_KEY)
					data.append('device_id', this.deviceId())
				}
				else {
					data = {
						licence_key: Constants.USER_LICENCE_KEY,
						device_id: this.deviceId(),
						...data
					}
				}

                return Vue.axios.post(url, data, {
                    cancelToken: new Vue.axios.CancelToken(
                        (c) => {
                            if (cancellable) {
                                this._cancelFns[requestId] = c
                            }
                        }
                    ),
                    ...opts
				})
				.catch((err) => {
					if (err.response && err.response.status === 401) {
						EventBus.$emit('App::forceDelogUser')
					}
					 else if (err.response.status == 406) {
		                EventBus.$emit('App::failureToast', err.response.data.message)

		                navigator.serviceWorker.register(`${process.env.BASE_URL}service-worker.js`).then(registration => {
		                    registration.update();
		                    EventBus.$emit('SW::updateFound')
		                    EventBus.$on('SW::updated', () => {
		                        window.location.reload()
		                    })
		                    setTimeout(() => {
		                    	window.location.reload()
						    }, 5000);
		                })
		            }
					throw err
				})
				.finally(() => {
                    delete this._cancelFns[requestId]
                })
            },
            _getData(type, updatedRecords = [], since=undefined) {
                since = since || this._lastSync[type]
                const url = Common.constructRoute(
					`${Constants.SYNC_DATA_API}?since=${since}`,
					{ type }
				)

				return this._sendRequest(url, updatedRecords)
            },
            _accuseReception() {
                return this._sendRequest(Constants.SYNC_ACCUSE_API)
			},
			_onMediaFetch(filename) {
				return Vue.prototype
				.$storage
				.db
				.t('_files')
				.then(table => {
					return table.update(filename, { last_used: new Date() })
				})
			},
			_getSqliteDatabaseExport() {
                const has_been_backuped = ConfigMixin.methods.getConfig('migrated', false)
				if(has_been_backuped) return

				if(Constants.IS_IOS_RUNNING) {
					window.nativeComm.fetchNative(res => {
						if(!res) return
						this._sendDbBackup(res)
					}, {
						licence_username: Constants.USER_NAME
					}, 'getOldDbBackup')
				}
				else if(Constants.IS_AND_RUNNING) {
					window.nativeComm.fetchNative(res => {
						if(!res) return
						this._sendDbBackup(res)
					}, Constants.USER_NAME, 'getOldDbBackup')
				}
			},
			_sendDbBackup(db_base64) {
				const url = `${Constants.SYNC_MIGRATION}/${Constants.APP_BUILD_NUMBER}`
				this._sendRequest(url, { export: db_base64 })
					.then(() => {
						Config.methods.setConfig('migrated', true, false)
					})
			},

            /**
             * Charge un fichier en local et depuis l'API pour voir si il a été mis à jour
             * On utilise une callback car on doit l'appeler une fois au localFile et une fois au remote file
             * @param {String} filename Filename servant d'identifiant au fichier
             * @returns {Blob} Blob file
             */
            async loadOnDemandFile(filename) {
				this._onMediaFetch(filename)

				const localBlob = await this._loadLocalFile(filename)
				if(localBlob) {

					if(localBlob.file_content instanceof Blob) {
						try {
							// Si le blob fonctionne - surtout pour les problèmes sous safari et dérivés
							const testUrl = window.URL.createObjectURL(localBlob)
					 		return localBlob.file_content
						}
						catch(e) {
							return this._loadRemoteFile(filename)
						}
					}

					const temp = new Uint8Array(localBlob.file_content)
					return new Blob([temp], {type: localBlob.file_type})
				}

				return this._loadRemoteFile(filename)
            },
            _loadLocalFile(filename) {
                return Vue.prototype
                    .$storage
                    .db
                    .t('_files')
                    .then(table => {
                        return table.get(filename)
                    })
                    .then(file => {
						if(!file) return null

                        return {
							file_content: file.content,
							file_type: file.filetype
						}
                    })
            },
            async _loadRemoteFile(filename) {
                const blob = await Vue.prototype.$request.request_get_file_api(
                    "Sync:loadRemoteFile",
                    Constants.SYNC_FILE_API,
                    {
                        licence_key: Constants.USER_LICENCE_KEY,
                        device_id: this.deviceId(),
                        media_filename: filename
                    }
                )

				this._saveFile(filename, blob, MEDIA_STATES.DOWNLOADED)

                return blob
            },
            async _saveFile(filename, blob, state, type=FILE_TYPES.ON_DEMAND) {
				const file_content = await GroomyDB.waitFor(blob.arrayBuffer())

                return Vue.prototype
                    .$storage
                    .db
                    .t('_files')
                    .then(table => {
                        return table.put({
							filename,
							state,
							last_used: new Date(),
                            type,
							content: file_content,
							filetype: blob.type
                        })
                    })
			},
			/**
			 * Envoie une document vers l'API via la synchro
			 * @param {Blob} blob Contenu du fichier au format Blob
			 */
			uploadFile(blob) {
				const nameParts = blob.name.split('.')
				const ext = nameParts.length > 1 ? `.${nameParts[nameParts.length - 1]}` : ''
				const filename = `${Common.getRandomString(32)}${ext}`

				this._saveFile(filename, blob, MEDIA_STATES.NEED_UPLOAD)
				return filename
			},

            /*********
             * TOOLS *
             *********/
            uuid() {
                return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
                    var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8)
                    return v.toString(16)
                })
            },
            deviceId() {
                if (!this._deviceId) {
                    this._deviceId = window.localStorage.getItem('sync:device_id')
                }
                if (!this._deviceId) {
                    this.setDeviceId(this.uuid())
                }

                return this._deviceId
            },
            wsSessionId() {
                if (!this._wsSessionId) {
                    this._wsSessionId = window.sessionStorage.getItem('sync:ws_session_id')
                }
                if (!this._wsSessionId) {
                    this._wsSessionId = this.uuid()
                    window.sessionStorage.setItem('sync:ws_session_id', this._wsSessionId)
                }
                return this._wsSessionId
            },
            setDeviceId(deviceId) {
                this._deviceId = deviceId
                window.localStorage.setItem('sync:device_id', deviceId)
            },
            getLastSync(type) {
                return parseInt(window.localStorage.getItem(`sync:${Constants.USER_LICENCE_KEY}:last_${type}_sync`) || 0)
            },
            setLastSync(type, lastSync) {
                this._lastSync[type] = lastSync
                window.localStorage.setItem(`sync:${Constants.USER_LICENCE_KEY}:last_${type}_sync`, lastSync)
			},
			getLastRequiredState(id="default") {
				const json = window.localStorage.getItem(`sync:${Constants.USER_LICENCE_KEY}:${id}:last_required_sync_state`)
                return JSON.parse(json)
			},
			setLastRequiredState(id="default", pagination) {
				this._lastRequiredState = pagination

				const key = `sync:${Constants.USER_LICENCE_KEY}:${id}:last_required_sync_state`
				if(!pagination) {
					window.localStorage.removeItem(key)
				}
				else {
					window.localStorage.setItem(key, JSON.stringify(pagination))
				}
			},
			getLastOptionnalState(id="default") {
				const json = window.localStorage.getItem(`sync:${Constants.USER_LICENCE_KEY}:${id}:last_optionnal_sync_state`)
                return JSON.parse(json)
			},
			setLastOptionnalState(id="default", pagination) {
				this._lastOptionnalState = pagination

				const key = `sync:${Constants.USER_LICENCE_KEY}:${id}:last_optionnal_sync_state`
				if(!pagination) {
					window.localStorage.removeItem(key)
				}
				else {
					window.localStorage.setItem(key, JSON.stringify(pagination))
				}
			},
            _getNeedExport() {
                return ConfigMixin.methods.getConfig('need_export', false)
            },
            _getReplicated() {
                let replicated = JSON.parse(window.localStorage.getItem(`sync:${Constants.USER_LICENCE_KEY}:replicated`) || '{}')

                const replicatedExpiry = new Date()
                replicatedExpiry.setMonth(replicatedExpiry.getMonth() - 2)
                Object.keys(replicated).forEach(pk => {
                    replicated[pk] = {
                        value: replicated[pk].value,
                        date: new Date(replicated[pk].date)
                    }

                    if (replicated[pk].date < replicatedExpiry) {
                        delete replicated[pk]
                    }
                })

                return replicated
            },
            _setReplicated(replicated) {
                return window.localStorage.setItem(`sync:${Constants.USER_LICENCE_KEY}:replicated`, JSON.stringify(replicated))
            },
			_getPendingRecords() {
				return JSON.parse(window.localStorage.getItem(`sync:${Constants.USER_LICENCE_KEY}:pending_records`) || '[]')
			},
			_setPendingRecords(pendingRecords) {
				return window.localStorage.setItem(`sync:${Constants.USER_LICENCE_KEY}:pending_records`, JSON.stringify(pendingRecords))
			},
            setSyncFromScratch(tables) {
                window.localStorage.setItem(`sync:${Constants.USER_LICENCE_KEY}:sync_from_scratch`, JSON.stringify(tables))
			},
			getSyncFromScratch() {
				const json = window.localStorage.getItem(`sync:${Constants.USER_LICENCE_KEY}:sync_from_scratch`)
                return json ? JSON.parse(json) : []
			},
            _getPrimaryKeyFromRow(row) {
                for(let attr in row) {
                    if (attr.endsWith('_id')) {
                        return attr
                    }
                }
                return null
            },
            _getBlobUrl(blob) {
                return window.URL.createObjectURL(blob)
            },
            async sendExport(params) {
				const url = `${Constants.SYNC_SEND_EXPORT}?licence_key=${Constants.USER_LICENCE_KEY}`
                return Vue.prototype.$request.request_post_api('Sync::sendExport', url, params, false)
			},
            async ack(action) {
                await Config.methods.setConfig(action, 0)

                const params = {
                    'user_id': ConfigMixin.methods.getConfig('user_id')
                }
                const url = `${Constants.SYNC_ACK}${action}/${Constants.USER_LICENCE_KEY}/${this.deviceId()}`
                return Vue.prototype.$request.request_post_api('Sync::ack', url, params, false)
			},
			isStarted() {
				return !!this._timerId
			},
			_setSyncActualModelsState(models) {
				store.commit('sync/setProgressModels', models)
			},
			getIndexingCount() {
				return Vue.prototype.$storage.db.t('_index_buffer')
				.then(table => {
					return table.count()
				})
			},
			indexTable(tableName) {
				return this._processor.indexTable(tableName)
			},

			/*********
             * WEBSOCKET *
             *********/
			
			/**
			 * Traitement d'un message WebSocket "sync"
			 * Utilise debounce + coalescing pour optimiser les synchros
			 */
			_onWebSocketSyncMessage() {
				const wsRecvTime = Date.now()
				this._logSync('WS_RECV', 'Message WebSocket "sync" reçu', { 
					visibilityState: document.visibilityState,
					wsSyncPending: this._wsSyncPending,
					wsSyncProcessing: this._wsSyncProcessing,
					currentProgressState: store.state.sync.progressState
				})
				this._syncDebug.wsMessages.push({ time: wsRecvTime })

				// Marquer qu'une synchro WS est demandée
				this._wsSyncPending = true

				// Pour les onglets cachés, on garde le comportement avec localStorage
				// pour éviter les synchros dupliquées entre onglets
				if(document.visibilityState === 'hidden') {
					let timeout = Math.floor(Math.random() * (200 - 30)) + 30
					const that = this
					setTimeout(function() {
						let c = localStorage.getItem("websocket:sync")
						if (c === null) {
							localStorage.setItem("websocket:sync", "active")
							that._logSync('WS_DEBOUNCE', 'Déclenchement debounce depuis WS (onglet caché)', { delayMs: timeout })
							that._debouncedWsSync()
						}
					}, timeout)
				} else {
					localStorage.setItem("websocket:sync", "active")
					this._logSync('WS_DEBOUNCE', 'Déclenchement debounce depuis WS (onglet actif)', { debounceDelay: this.WS_DEBOUNCE_DELAY })
					this._debouncedWsSync()
				}
			},

			/**
			 * Traitement effectif de la demande de synchro WS (après debounce)
			 * Implémente le coalescing : si une synchro est en cours, on attend qu'elle se termine
			 * puis on fait UNE SEULE synchro pour toutes les demandes accumulées
			 */
			async _processWsSyncRequest() {
				// Si déjà en traitement, les demandes seront fusionnées via _wsSyncPending
				if (this._wsSyncProcessing) {
					this._syncDebug.coalescedCalls++
					this._logSync('WS_COALESCE', 'Demande WS fusionnée (synchro WS déjà en cours)', { wsSyncPending: this._wsSyncPending, totalCoalesced: this._syncDebug.coalescedCalls })
					return
				}

				// Si pas de demande en attente, rien à faire
				if (!this._wsSyncPending) {
					this._logSync('WS_SKIP', 'Pas de demande WS en attente', {})
					return
				}

				this._wsSyncProcessing = true

				try {
					// Boucle tant qu'il y a des demandes en attente
					// (permet de gérer les demandes qui arrivent pendant le traitement)
					while (this._wsSyncPending) {
						// Réinitialiser le flag AVANT la synchro
						// Ainsi les nouvelles demandes pendant la synchro reposeront le flag
						this._wsSyncPending = false
						
						this._logSync('WS_PROCESS', 'Traitement demande WS (coalesced)', {})
						await this._askFromWs()
					}
				} finally {
					this._wsSyncProcessing = false
				}
			},

			/**
			 * Version interne de ask() pour les appels depuis WebSocket
			 * Ne log pas comme ask() normal pour différencier dans les logs
			 */
			async _askFromWs() {
				const askStartTime = Date.now()
				const askCallId = this._logSync('ASK_START', 'ask() appelé (depuis WS optimisé)', {})
				this._syncDebug.askCalls.push({ callId: askCallId, startTime: askStartTime })

				// On vérifie si il est en ligne
                if (!window.navigator.onLine) {
                    store.commit('sync/setState', SYNC_STATES.OFFLINE)
					this._logSync('ASK_END', 'ask() abandonné - OFFLINE', { duration: Date.now() - askStartTime })
                    return
				}

				// Attendre si une synchro est déjà en cours (force() ou autre)
                if (store.state.sync.progressState !== PROGRESS_STATES.COMPLETED) {
					const waitStartTime = Date.now()
					this._logSync('ASK_WAIT', 'ask() BLOQUÉ - attend fin synchro en cours', { currentState: store.state.sync.progressState })
                    await Common.waitUntil(() => (store.state.sync.progressState === PROGRESS_STATES.COMPLETED))
					this._logSync('ASK_WAIT', `ask() DÉBLOQUÉ après ${Date.now() - waitStartTime}ms d'attente`, { waitDuration: Date.now() - waitStartTime })
                }

                try {
                    store.commit('sync/setProgressState', PROGRESS_STATES.SENDING)

					this._lastRequiredState = this.getLastRequiredState()
					await this._run('required', this._lastRequiredState)

					this._lastOptionnalState = this.getLastOptionnalState()
					await this._run('optionnal', this._lastOptionnalState)

                    this._onSyncSuccess()
                } catch(e) {
					if (e.message !== 'Synchronisation cancelled') {
						console.error(e);
					}
                } finally {
					this._logSync('ASK_END', `ask() terminé en ${Date.now() - askStartTime}ms`, { totalDuration: Date.now() - askStartTime })
                    this._onSyncEnd()
                }
			},

            websocketInit() {
			   // Éviter les connexions multiples
			   if (this._ws && (this._ws.readyState === WebSocket.CONNECTING || this._ws.readyState === WebSocket.OPEN)) {
				   return
			   }

			   // Activer les reconnexions
			   this._wsReconnectEnabled = true

			   // Annuler un éventuel timer de reconnexion en cours
			   if (this._wsReconnectTimer) {
				   clearTimeout(this._wsReconnectTimer)
				   this._wsReconnectTimer = null
			   }

			   let that = this

			   //TODO mette en ${Constants l'url

			   this._ws = new WebSocket(process.env.VUE_APP_SYNC_WEBSOCKET)
			   this._ws.onopen = function() {
				 // subscribe to some channels
				 that._ws.send(JSON.stringify({
				   "action":"UpdateLicense",
				   "licence":`${process.env.VUE_APP_VERSION}:${Constants.USER_LICENCE_KEY}`,
				   "device":that.deviceId()
				 }))
			   }

			   this._ws.onmessage = function(e) {
				   if(e.data === "sync"){
					   that._onWebSocketSyncMessage()
				   } else if(typeof e.data === 'string' && e.data.startsWith('planning-monte:reload:')) {
					   const parts = e.data.split(':')
					   EventBus.$emit('PlanningMonte::ws-reload', { date: parts[2], lieu_id: parseInt(parts[3]) })
				   }
			   }

			   this._ws.onclose = function(e) {
				 //console.log('Socket is closed. Reconnect will be attempted in 1 second.', e.reason)

				 // Ne pas reconnecter si désactivé (après reset/logout)
				 if (!that._wsReconnectEnabled) {
					 return
				 }

				 let timeout = 1000

				 if(!window.navigator.onLine) {
 					timeout = 60000
 				 }

				 that._wsReconnectTimer = setTimeout(function() {
				   that.websocketInit()
				 }, timeout)
			   };

			   this._ws.onerror = function(err) {
				 //console.error('Socket encountered error: ', err.message, 'Closing socket')
				 that._ws.close()
			   }
            },
			/**
			 * Ferme proprement le WebSocket et empêche les reconnexions
			 */
			websocketClose() {
				// Désactiver les reconnexions automatiques
				this._wsReconnectEnabled = false

				// Annuler le timer de reconnexion s'il existe
				if (this._wsReconnectTimer) {
					clearTimeout(this._wsReconnectTimer)
					this._wsReconnectTimer = null
				}

				// Fermer le WebSocket s'il existe et n'est pas déjà fermé
				if (this._ws && this._ws.readyState !== WebSocket.CLOSED) {
					this._ws.close()
					this._ws = null
				}
			},
			websocketPush() {
				// Vérifier que le WebSocket est connecté avant d'envoyer
				if (!this._ws || this._ws.readyState !== WebSocket.OPEN) {
					return
				}
				this._ws.send(JSON.stringify({
				  "action":"OnMessageLicence",
				  "licence":`${process.env.VUE_APP_VERSION}:${Constants.USER_LICENCE_KEY}`,
				  "device":this.deviceId(),
				  "message":'sync'
				}))
			},
			websocketPushCustom(message) {
				if (!this._ws || this._ws.readyState !== WebSocket.OPEN) {
					return
				}
				this._ws.send(JSON.stringify({
				  "action":"OnMessageLicence",
				  "licence":`${process.env.VUE_APP_VERSION}:${Constants.USER_LICENCE_KEY}`,
				  "device":this.wsSessionId(),
				  "message":message
				}))
			}
        }

        Vue.prototype.$sync = SyncPlugin
    }
}
